import { useCallback, useEffect, useState } from 'react';

import client from '../api/client';

// Valid FULFILLMENT statuses (server: models/order/Order.js STATUSES).
// Payment ('pending'/'balance_due'/'paid') is a separate, independent field —
// see PAYMENT_STATUSES — set automatically by Stripe webhooks.
export const ORDER_STATUSES = [
  'pending',
  'processing',
  'shipped',
  'ready_for_pickup',
  'picked_up',
  'completed',
  'cancelled',
  'refunded',
];

// Valid payment statuses (server: order.paymentStatus).
export const PAYMENT_STATUSES = ['pending', 'balance_due', 'paid'];

// For a free-choice status <select> on the general Ship page, which lists
// every order (shipped and pickup alike). 'picked_up' and 'ready_for_pickup'
// are excluded: the server only allows either when the order actually
// requiresPickup, so they belong on the pickup-specific workflow
// (admin/Pickups.js) instead of an arbitrary dropdown choice here.
export const MANUAL_STATUS_OPTIONS = ORDER_STATUSES.filter((s) => s !== 'picked_up' && s !== 'ready_for_pickup');

// Admin order management (/api/admin, requireAdmin).
// GET /admin/orders?status=&paymentStatus=&requiresPickup=&requiresShipping=&page=&limit= -> { items, page, limit, total, pages }.
// status and paymentStatus filter independently and can be combined.
// PUT /admin/orders/:id/status { status } updates fulfillment status only and appends to statusHistory.
// DELETE /admin/orders/:id soft-deletes an order (recoverable — see useAdminDeletedOrders).
const EMPTY = { items: [], page: 1, limit: 20, total: 0, pages: 1 };

export default function useAdminOrders({
  status = '',
  paymentStatus = '',
  requiresPickup = false,
  requiresShipping = false,
  page = 1,
  limit = 20,
} = {}) {
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const buildParams = useCallback(() => {
    const params = { page, limit };
    if (status) params.status = status;
    if (paymentStatus) params.paymentStatus = paymentStatus;
    if (requiresPickup) params.requiresPickup = 'true';
    if (requiresShipping) params.requiresShipping = 'true';
    return params;
  }, [status, paymentStatus, requiresPickup, requiresShipping, page, limit]);

  const reload = useCallback(async () => {
    const { data: res } = await client.get('/admin/orders', { params: buildParams() });
    setData(res && Array.isArray(res.items) ? res : EMPTY);
  }, [buildParams]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    client
      .get('/admin/orders', { params: buildParams() })
      .then(({ data: res }) => {
        if (active) setData(res && Array.isArray(res.items) ? res : EMPTY);
      })
      .catch((err) => {
        if (active) {
          setData(EMPTY);
          setError(err);
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [buildParams]);

  const updateStatus = useCallback(
    async (id, nextStatus) => {
      const { data: res } = await client.put(`/admin/orders/${id}/status`, { status: nextStatus });
      await reload();
      return res;
    },
    [reload]
  );

  const deleteOrder = useCallback(
    async (id) => {
      const { data: res } = await client.delete(`/admin/orders/${id}`);
      await reload();
      return res;
    },
    [reload]
  );

  return { ...data, loading, error, reload, updateStatus, deleteOrder };
}
